import { useTenantPaymentTerms } from '../../utils/productCatalog';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { asObjectId } from '../../utils/references';
import { seedPgma } from './reviseSeed';
import { defaultEffectiveDate } from './offerDefaults';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const DEFAULT_POLICYHOLDER = {
  companyName: '',
  address: '',
  phone: '',
  email: '',
  branch: '',
  lastName: '',
  firstName: '',
  dateOfBirth: '',
  gender: '',
};

const DEFAULT_PRODUCT = {
  annualSalary: '300000',
  coverageType: 'maladie_accident',
  waitingPeriod: '30',
  effectiveDate: defaultEffectiveDate(),
};

const usePgmaOfferFunctions = ({ onCreated, onClose, reviseFrom = null, editDraft = null }) => {
  // Revise mode: seed every field from the offer being revised so nothing
  // has to be re-entered. Computed once via the lazy useState initialiser
  // — re-deriving it on each render would fight the user's own edits.
  // Seeded from the offer being REVISED (a new version of an issued
  // proposal) or the DRAFT being edited in place. Same reconstruction
  // either way — the difference is only what the save call does, so the
  // two flows cannot drift in what they carry over.
  const seedSource = reviseFrom || editDraft;
  const seed = seedSource ? seedPgma(seedSource) : null;
  const [policyholder, setPolicyholder] = useState(() => seed?.policyholder || DEFAULT_POLICYHOLDER);
  const [clientId, setClientId] = useState(() => seed?.clientId || '');
  const [brokerId, setBrokerId] = useState(() => seed?.brokerId || '');
  const [productConfig, setProductConfig] = useState(() => (
    seed ? { ...DEFAULT_PRODUCT, ...seed.productConfig } : DEFAULT_PRODUCT
  ));
  const [calc, setCalc] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState('');
  const [ageNotEligible, setAgeNotEligible] = useState(null); // {message, age} when 70+
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const debounceRef = useRef(null);
  // Tracks policyholder fields the last selectClient() populated. Keeps them
  // eligible for overwrite on the next selection, without touching anything
  // the user has since typed manually.
  const autoFilledRef = useRef(new Set());

  const updatePolicyholder = useCallback((field, value) => {
    // A manual edit drops the field from the auto-filled set so a later
    // client reselection cannot overwrite the user's typing.
    autoFilledRef.current.delete(field);
    setPolicyholder(prev => ({ ...prev, [field]: value }));
  }, []);

  // Prefill policyholder fields from the selected Client. Writes into any
  // field that is empty OR was previously auto-filled by an earlier client
  // selection; never touches values the user has typed themselves.
  // PGMA policyholder is an individual (doctor) with company + personal fields.
  const selectClient = useCallback((client) => {
    if (!client) { setClientId(''); return; }
    setClientId(client._id);
    setPolicyholder(prev => {
      const next = { ...prev };
      const newlyFilled = new Set();
      const trySet = (field, value) => {
        const wasAuto  = autoFilledRef.current.has(field);
        const canWrite = !prev[field] || wasAuto;
        if (!canWrite) return;             // user typed here — leave alone
        if (!value) {
          if (wasAuto) next[field] = '';   // clear stale prior-client data
          return;
        }
        next[field] = value;
        newlyFilled.add(field);
      };
      const addr = client.address || {};
      const addrLine = [addr.street, addr.postalCode, addr.city, addr.country]
        .map(s => (s || '').trim()).filter(Boolean).join(', ');
      const dob = client.dateOfBirth
        ? new Date(client.dateOfBirth).toISOString().slice(0, 10)
        : '';
      trySet('companyName', client.companyName || client.displayName || '');
      trySet('address',     addrLine);
      trySet('email',       client.email);
      trySet('phone',       client.phone);
      trySet('firstName',   client.firstName);
      trySet('lastName',    client.lastName);
      trySet('dateOfBirth', dob);
      trySet('gender',      client.gender);
      autoFilledRef.current = newlyFilled;
      return next;
    });
  }, []);
  const clearClient = useCallback(() => setClientId(''), []);
  const updateProduct = useCallback((field, value) => {
    setProductConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  const canCalculate = () => Boolean(
    productConfig.annualSalary && Number(productConfig.annualSalary) > 0 &&
    productConfig.coverageType &&
    productConfig.waitingPeriod &&
    policyholder.dateOfBirth,
  );

  const runCalculate = useCallback(async () => {
    if (!canCalculate()) {
      setCalc(null);
      setAgeNotEligible(null);
      setCalcError('');
      return;
    }
    setCalcLoading(true);
    setCalcError('');
    setAgeNotEligible(null);
    try {
      const resp = await axios.post(
        `${BASE_URL}/offers/pgma/calculate`,
        {
          annualSalary: Number(productConfig.annualSalary),
          coverageType: productConfig.coverageType,
          waitingPeriod: Number(productConfig.waitingPeriod),
          dateOfBirth: policyholder.dateOfBirth,
          effectiveDate: productConfig.effectiveDate,
          gender: policyholder.gender || undefined,
        },
        { withCredentials: true },
      );
      setCalc(resp.data?.data);
    } catch (err) {
      const body = err.response?.data;
      if (body?.code === 'AGE_NOT_ELIGIBLE') {
        setAgeNotEligible({ message: body.message, age: body.age });
        setCalc(null);
      } else {
        setCalcError(body?.message || 'Could not calculate premium');
        setCalc(null);
      }
    } finally {
      setCalcLoading(false);
    }
  }, [productConfig.annualSalary, productConfig.coverageType, productConfig.waitingPeriod, productConfig.effectiveDate, policyholder.dateOfBirth, policyholder.gender]);

  // Debounced auto-recalculate when relevant inputs change.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runCalculate, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [runCalculate]);

  const submit = useCallback(async (status = 'sent') => {
    setSaving(true);
    setSaveError('');
    try {
      const resp = await axios.post(
        `${BASE_URL}/offers/pgma`,
        {
          policyholder,
          clientId: asObjectId(clientId) || undefined,
          // Only a real id is ever posted as a reference — never a display value.
          ...(asObjectId(brokerId) && { broker: asObjectId(brokerId) }),
          productConfig: {
            annualSalary: Number(productConfig.annualSalary),
            coverageType: productConfig.coverageType,
            waitingPeriod: Number(productConfig.waitingPeriod),
            effectiveDate: productConfig.effectiveDate,
            // Sent at last: the server resolves it against the tenant's PGMA
            // paymentTerms (omitted = the configured default).
            ...(productConfig.paymentFrequency && { paymentFrequency: productConfig.paymentFrequency }),
          },
          status,
          ...(reviseFrom?._id && { revisionOf: reviseFrom._id }),
          // Editing a draft returns to the SAME product endpoint with the
          // draft's id, so the premium and the params snapshot are rebuilt
          // by the identical code that built them the first time. The
          // server updates in place: same _id, same proposal number.
          ...(editDraft?._id && { draftId: editDraft._id }),
        },
        { withCredentials: true },
      );
      onCreated && onCreated(resp.data?.data);
      onClose && onClose();
    } catch (err) {
      const body = err.response?.data;
      if (body?.code === 'AGE_NOT_ELIGIBLE') {
        setAgeNotEligible({ message: body.message, age: body.age });
      } else {
        setSaveError(body?.message || 'Failed to create offer');
      }
    } finally {
      setSaving(false);
    }
  }, [policyholder, clientId, brokerId, productConfig, reviseFrom, editDraft, onCreated, onClose]);

  // The cap limits the rating basis, not what may be declared: a
  // practitioner earning above it is insurable, their premium is simply
  // rated on the cap. So this is a disclosure, never a gate — the user
  // keeps the salary they typed and the form stays submittable. The cap
  // itself lives on the tenant's rate table (constants.maxSalary), so
  // `effectiveSalary` when capped IS the cap: no second fetch and no risk
  // of the UI disagreeing with the engine.
  const salaryCapped = Boolean(calc?.inputs?.salaryCapped);
  const insuredSalary = salaryCapped ? calc.inputs.effectiveSalary : null;
  const enteredSalary = salaryCapped ? calc.inputs.annualSalary : null;

  // PGMA resolves its own terms exactly as RC Pro and PGM do — shared
  // resolver, no product conditional.
  const paymentTerms = useTenantPaymentTerms('PGMA');

  return {
    paymentTerms,
    policyholder, updatePolicyholder,
    clientId, selectClient, clearClient,
    brokerId, setBrokerId,
    productConfig, updateProduct,
    calc, calcLoading, calcError, ageNotEligible,
    canCalculate: canCalculate(),
    salaryCapped, insuredSalary, enteredSalary,
    saving, saveError,
    runCalculate,
    saveDraft: () => submit('draft'),
    createOffer: () => submit('sent'),
  };
};

export default usePgmaOfferFunctions;
